import { useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth, roleHome } from "../AuthContext";
import Brand from "../../components/site/Brand";

export default function Login() {
  const { login, demoLogin, status, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [error, setError] = useState("");

  const destination = (u) => next && next.startsWith("/app/") ? next : roleHome(u);

  if (status === "authed") return <Navigate to={destination(user)} replace />;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(email, password);
      navigate(destination(data?.user || user));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function tryDemo() {
    setDemoBusy(true);
    setError("");
    try {
      const data = await demoLogin();
      navigate(destination(data?.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setDemoBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center px-5 py-10" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Brand tone="dark" />
        </div>

        <div className="bg-white rounded-2xl shadow-[0_20px_50px_-25px_rgba(11,18,32,0.25)] border border-slate-200 p-8">
          <h1 className="font-display text-2xl md:text-3xl tracking-tight text-slate-900 font-semibold">
            Welcome back.
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Sign in to your compliance workspace.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
                Work email
              </label>
              <input
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.co.uk"
                className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
                Password
              </label>
              <input
                type="password"
                required
                minLength={8}
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-3 text-sm font-semibold transition-colors"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight size={14} />
            </button>

            <div className="text-center">
              <Link
                to="/app/forgot-password"
                data-testid="login-forgot-password"
                className="text-[12.5px] text-slate-500 hover:text-[#8C6B33] font-medium"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            onClick={tryDemo}
            disabled={demoBusy}
            data-testid="login-try-demo"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#D4A65A]/40 bg-[#D4A65A]/10 hover:bg-[#D4A65A]/15 disabled:opacity-40 text-[#8C6B33] px-4 py-3 text-sm font-semibold transition-colors"
          >
            <ShieldCheck size={14} />
            {demoBusy ? "Loading demo…" : "Explore the demo workspace"}
          </button>
          <p className="mt-2 text-[11.5px] text-slate-500 text-center">
            No account needed — see UK Cannabis Support Line running the platform.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          New to CanniComply?{" "}
          <Link to="/app/signup" data-testid="login-goto-signup" className="text-[#8C6B33] hover:text-[#0B1220] font-semibold">
            Create your workspace
          </Link>
        </p>
      </div>
    </div>
  );
}
